import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  Users,
  Plus,
  Search,
  MessageSquare,
  Globe,
  Lock,
  UserPlus,
  Settings,
  TrendingUp,
  Clock,
  Filter,
  Lightbulb,
  Award
} from 'lucide-react';
import GuestBanner from '../components/GuestBanner';
import GuestAccessibleBadge from '../components/GuestAccessibleBadge';
import usePageTitle from '../hooks/usePageTitle';

const API = process.env.REACT_APP_BACKEND_URL;

// Type icon mapping
const TYPE_ICONS = {
  general: '💬',
  breed: '🐕',
  topic: '📚',
  location: '📍',
  activity: '🏃',
  health: '🏥',
  rescue: '🏠',
  marketplace: '🛒',
  events: '📅',
  showcase: '⭐'
};

const TYPE_LABELS = {
  general: 'General',
  breed: 'Breed',
  topic: 'Topic',
  location: 'Location',
  activity: 'Activity',
  health: 'Health',
  rescue: 'Rescue',
  marketplace: 'Marketplace',
  events: 'Events',
  showcase: 'Showcase'
};

const SubGroupCard = ({ subgroup, onJoin }) => {
  const { user } = useAuth();
  const navigate = useNavigate();

  return (
    <Card 
      className="hover:shadow-md transition-all cursor-pointer group"
      onClick={() => navigate(`/communities/${subgroup.id}`)}
      data-testid={`subgroup-card-${subgroup.slug}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <div 
            className="w-14 h-14 rounded-xl flex items-center justify-center text-2xl shrink-0"
            style={{ backgroundColor: `${subgroup.color}20` }}
          >
            {subgroup.icon_emoji}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold truncate group-hover:text-primary transition-colors">
                {subgroup.name}
              </h3>
              {subgroup.visibility === 'private' && <Lock className="w-4 h-4 text-muted-foreground" />}
              {subgroup.visibility === 'public' && <Globe className="w-3 h-3 text-emerald-600" />}
            </div>
            <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
              {subgroup.description}
            </p>
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Users className="w-3 h-3" />
                {subgroup.member_count} members
              </span>
              <span className="flex items-center gap-1">
                <MessageSquare className="w-3 h-3" />
                {subgroup.post_count} posts
              </span>
              <Badge variant="outline" className="text-xs">
                {TYPE_ICONS[subgroup.subgroup_type]} {TYPE_LABELS[subgroup.subgroup_type]}
              </Badge>
            </div>
          </div>
          {user && !subgroup.is_member && (
            <Button 
              size="sm" 
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                onJoin(subgroup.id);
              }}
              data-testid={`join-${subgroup.slug}`}
            >
              <UserPlus className="w-4 h-4 mr-1" />
              Join
            </Button>
          )}
          {subgroup.is_member && (
            <Badge variant="secondary" className="shrink-0">Joined</Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const SubGroups = () => {
  usePageTitle('Communities');
  const { user } = useAuth();
  const { clubId } = useParams();
  const navigate = useNavigate();
  
  const [subgroups, setSubgroups] = useState([]);
  const [mySubgroups, setMySubgroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [activeTab, setActiveTab] = useState('discover');

  const fetchSubgroups = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (clubId) params.append('club_id', clubId);
      if (searchQuery) params.append('search', searchQuery);
      if (filterType !== 'all') params.append('subgroup_type', filterType);

      const response = await axios.get(`${API}/api/subgroups?${params}`);
      setSubgroups(response.data);
    } catch (error) {
      console.error('Error fetching subgroups:', error);
    } finally {
      setLoading(false);
    }
  }, [clubId, searchQuery, filterType]);

  const fetchMySubgroups = useCallback(async () => {
    if (!user) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/subgroups/my-subgroups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMySubgroups(response.data);
    } catch (error) {
      console.error('Error fetching my subgroups:', error);
    }
  }, [user]);

  useEffect(() => {
    fetchSubgroups();
  }, [fetchSubgroups]);

  useEffect(() => {
    if (user) {
      fetchMySubgroups();
    }
  }, [user, fetchMySubgroups]);

  const handleJoin = async (subgroupId) => {
    if (!user) {
      navigate('/login', { state: { from: '/communities' } });
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/subgroups/${subgroupId}/join`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Refresh lists
      fetchSubgroups();
      fetchMySubgroups();
    } catch (error) {
      console.error('Error joining subgroup:', error);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-4xl font-heading font-medium">Communities</h1>
              <GuestAccessibleBadge />
            </div>
            <p className="text-muted-foreground text-lg">
              Join topic-based communities within your clubs
            </p>
          </div>
          {user && (
            <Button onClick={() => navigate('/communities/create')} data-testid="create-community-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Community
            </Button>
          )}
        </div>

        <GuestBanner message="Join to create and participate in communities" />

        {/* Growth Path Info */}
        <Alert className="mb-6 bg-gradient-to-r from-primary/5 to-primary/10 border-primary/20">
          <Award className="h-4 w-4 text-primary" />
          <AlertDescription className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <span>
              <strong>Build your own club!</strong> Communities that grow to 50+ members can graduate 
              to fully independent clubs with their own URL, bylaws, and branding.
            </span>
            <Button 
              size="sm" 
              variant="outline" 
              onClick={() => navigate('/suggest-club')}
              className="shrink-0"
            >
              <Lightbulb className="w-3 h-3 mr-1" />
              Suggest a Club
            </Button>
          </AlertDescription>
        </Alert>

        {/* Search and Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search communities..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-communities"
            />
          </div>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2 border rounded-md bg-background"
            data-testid="filter-type"
          >
            <option value="all">All Types</option>
            {Object.entries(TYPE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {TYPE_ICONS[value]} {label}
              </option>
            ))}
          </select>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList>
            <TabsTrigger value="discover" data-testid="tab-discover">
              <Globe className="w-4 h-4 mr-2" />
              Discover
            </TabsTrigger>
            {user && (
              <TabsTrigger value="my-communities" data-testid="tab-my-communities">
                <Users className="w-4 h-4 mr-2" />
                My Communities ({mySubgroups.length})
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="discover" className="space-y-4">
            {loading ? (
              <div className="grid gap-4 md:grid-cols-2">
                {[1, 2, 3, 4].map((i) => (
                  <Card key={i} className="animate-pulse">
                    <CardContent className="p-4">
                      <div className="flex gap-4">
                        <div className="w-14 h-14 bg-muted rounded-xl" />
                        <div className="flex-1 space-y-2">
                          <div className="h-4 bg-muted rounded w-1/3" />
                          <div className="h-3 bg-muted rounded w-2/3" />
                          <div className="h-3 bg-muted rounded w-1/2" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : subgroups.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium mb-2">No communities yet</h3>
                  <p className="text-muted-foreground mb-4">
                    Be the first to create a community for your club!
                  </p>
                  {user && (
                    <Button onClick={() => navigate('/communities/create')}>
                      <Plus className="w-4 h-4 mr-2" />
                      Create Community
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {subgroups.map((subgroup) => (
                  <SubGroupCard 
                    key={subgroup.id} 
                    subgroup={subgroup} 
                    onJoin={handleJoin}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          {user && (
            <TabsContent value="my-communities" className="space-y-4">
              {mySubgroups.length === 0 ? (
                <Card>
                  <CardContent className="p-12 text-center">
                    <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="text-lg font-medium mb-2">No communities joined yet</h3>
                    <p className="text-muted-foreground mb-4">
                      Explore and join communities that interest you!
                    </p>
                    <Button onClick={() => setActiveTab('discover')}>
                      <Globe className="w-4 h-4 mr-2" />
                      Discover Communities
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-4 md:grid-cols-2">
                  {mySubgroups.map((subgroup) => (
                    <SubGroupCard 
                      key={subgroup.id} 
                      subgroup={subgroup} 
                      onJoin={handleJoin}
                    />
                  ))}
                </div>
              )}
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
};

export default SubGroups;
